/**
 * Image Control
 * Display images with alt text and sizing options
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';

const ImageControl = memo<ControlRendererProps>(({ control, value }) => {
  const containerClassName = getBaseControlClassName(control, {
    disabled: control.disabled,
  });

  // Get image source from value or config
  const src = (value || control.config?.src || control.defaultValue) as string;

  // Get sizing options from config
  const width = (control.config?.width as string | number) || 'auto';
  const height = (control.config?.height as string | number) || 'auto';
  const maxWidth = (control.config?.maxWidth as string) || '100%';
  const alt = (control.config?.alt as string) || control.label || 'Image';
  const objectFit =
    (control.config?.objectFit as string) || 'contain';

  const imageStyle = useMemo(() => {
    return {
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
      maxWidth,
      objectFit: objectFit as React.CSSProperties['objectFit'],
    };
  }, [width, height, maxWidth, objectFit]);

  if (!src) {
    return (
      <div className={containerClassName}>
        <div className="control__image-placeholder" style={{ ...imageStyle }}>
          <div style={{ color: '#9CA3AF', textAlign: 'center' }}>
            No image provided
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {control.label && (
        <div className="control__label">{control.label}</div>
      )}

      <div className="control__image-wrapper">
        <img
          id={control.id}
          src={src}
          alt={alt}
          style={imageStyle}
          className="control__image"
          loading="lazy"
        />
      </div>

      {control.helpText && (
        <div className="control__help">{control.helpText}</div>
      )}
    </div>
  );
});

ImageControl.displayName = 'ImageControl';

export default ImageControl;
